/**
 * Recommended products slider for the cart drawer.
 *
 * <cart-recommendations> starts out empty and pulls its markup from the Product
 * Recommendations API, which answers with the `cart-recommendations` section
 * rendered for the product given in `data-url`.
 *
 * Swiper is loaded as a classic deferred script before this module, so the
 * global is guaranteed to be there by the time this runs: deferred classic
 * scripts and module scripts execute in document order.
 */

const SWIPER_OPTIONS = {
  slidesPerView: 2,
  spaceBetween: 12,
  // Nothing to drag when everything already fits.
  watchOverflow: true,
  a11y: { enabled: true }
};

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    // The drawer re-renders itself through innerHTML, so each refresh brings a
    // new element with a URL built from the current last line item.
    this.load();
  }

  disconnectedCallback() {
    this.swiper?.destroy();
    this.swiper = null;
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.statusText);

      const content = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('.cart-recs__content');

      // No content means no recommendations for this product — stay empty.
      if (!content || !this.isConnected) return;

      this.replaceChildren(content);

      // The cards arrive after promo-products.js has already run over the page,
      // so promo pricing and countdowns are applied to them here.
      window.promoSystem?.run().catch(() => {});

      // Navigation is passed as elements, not selectors: selectors would be
      // looked up on the document and could pick up another drawer's buttons.
      this.swiper = new Swiper(this.querySelector('.cart-recs__swiper'), {
        ...SWIPER_OPTIONS,
        navigation: {
          prevEl: this.querySelector('.cart-recs__nav--prev'),
          nextEl: this.querySelector('.cart-recs__nav--next')
        }
      });
    } catch {
      // Recommendations are decoration: a failure leaves the drawer as it is.
    }
  }
}

customElements.define('cart-recommendations', CartRecommendations);
